/**
 * 後台 — 會員列表（#4 360 基本版）
 *
 * 篩選：status / tier / tag（簡易 UI）
 * CSV 匯出按鈕
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminDeleteMember, adminListMemberTags, adminListReferralSources, adminSetMemberStatus, exportMembersCsv, listMembers, type ReferralSource } from "../../api/admin";
import { type Member } from "../../api/members";
import AdminHeader from "../../components/AdminHeader";
import DataTable, { Column } from "../../components/DataTable";
import MemberAvatar from "../../components/MemberAvatar";
import TierBadge, { TIER_LABEL } from "../../components/TierBadge";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";

const STATUS_LABEL: Record<string, string> = {
  pending: "待入會",
  active: "正常",
  suspended: "停用",
  merged: "已合併",
};

export default function MemberList() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [rows, setRows] = useState<Member[]>([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [filterReferral, setFilterReferral] = useState("");
  const [referralSources, setReferralSources] = useState<ReferralSource[]>([]);
  const [filterTag, setFilterTag] = useState("");
  const [memberTags, setMemberTags] = useState<{ tag: string; count: number }[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterTier, filterReferral, filterTag, role]);

  // 推薦來源清單 — 從會員實際 referred_by 聚合(含顧問碼 / 舊會員碼 / 手打碼),給篩選下拉用,載一次
  useEffect(() => {
    if (role !== "admin") return;
    adminListReferralSources()
      .then((r) => setReferralSources(r.sources))
      .catch(() => {});
  }, [role]);

  // 標籤清單 — 偏好問卷自動寫入(對象:/品項:/頻率:)+ 後台手動貼的標籤,載一次(同推薦來源做法)
  useEffect(() => {
    if (role !== "admin") return;
    adminListMemberTags()
      .then((r) => setMemberTags(r.tags))
      .catch(() => {});
  }, [role]);

  // search 用 debounce — 邊打字邊查太頻繁,400ms 沒新輸入才打 API
  useEffect(() => {
    const timer = setTimeout(() => {
      if (role === "admin") refresh();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function refresh() {
    setLoading(true);
    try {
      const r = await listMembers({
        status: filterStatus || undefined,
        tier: filterTier || undefined,
        search: search.trim() || undefined,
        referred_by_code: filterReferral || undefined,
        tag: filterTag || undefined,
        limit: 100,
      });
      setRows(r.members);
      setSelected(new Set());     // 換 filter 清空選取
    } catch {
      useUIStore.getState().showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.line_user_id || "")),
    );
  }

  async function bulkSetStatus(target: "active" | "suspended") {
    const ids = Array.from(selected).filter(Boolean);
    if (!ids.length) return;
    const label = target === "active" ? "啟用" : "停用";
    if (!confirm(`確認對勾選的 ${ids.length} 位會員${label}?`)) return;
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        const r = await adminSetMemberStatus(id, target);
        if (r.status === "ok") ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setBulkRunning(false);
    setSelected(new Set());
    useUIStore.getState().showToast(
      fail === 0 ? `已${label} ${ok} 位` : `成功 ${ok} 位,失敗 ${fail} 位`,
      fail === 0 ? "success" : "error",
    );
    refresh();
  }

  async function bulkDelete() {
    const ids = Array.from(selected).filter(Boolean);
    if (!ids.length) return;
    if (!confirm(`永久刪除勾選的 ${ids.length} 位會員?不可復原。\n有訂單 / 存摺餘額的會被擋下,可選擇是否強制刪。`)) return;
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    const hasHistory: string[] = [];
    for (const id of ids) {
      try {
        const r = await adminDeleteMember(id, false);
        if (r.status === "ok") ok++;
        else if (r.status === "has_history") hasHistory.push(id);
        else fail++;
      } catch {
        fail++;
      }
    }
    // 有 history 的二次確認 → 強制刪
    if (hasHistory.length > 0) {
      const yes = confirm(
        `${hasHistory.length} 位有訂單或錢包餘額(已被擋下)。\n強制刪除這些會員?連同他們的歷史資料一併清掉,不可復原。`,
      );
      if (yes) {
        for (const id of hasHistory) {
          try {
            const r = await adminDeleteMember(id, true);
            if (r.status === "ok") ok++;
            else fail++;
          } catch {
            fail++;
          }
        }
      } else {
        fail += hasHistory.length;     // 沒強制刪 → 算未完成
      }
    }
    setBulkRunning(false);
    setSelected(new Set());
    useUIStore.getState().showToast(
      fail === 0 ? `已刪除 ${ok} 位` : `成功 ${ok} 位,失敗 / 未處理 ${fail} 位`,
      fail === 0 ? "success" : "error",
    );
    refresh();
  }

  async function exportCsv() {
    try {
      const blob = await exportMembersCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yannwo_members_${todayTpe()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      useUIStore.getState().showToast("匯出失敗", "error");
    }
  }

  const columns: Column<Member>[] = [
    {
      key: "name",
      header: "姓名",
      // 頭像 + 真名優先;LINE 暱稱跟真名不同時才在右邊補灰色小字(客戶靠頭像 + 暱稱認人)
      render: (r) => {
        const real = (r.name || "").trim();
        const nick = (r.display_name || "").trim();
        return (
          <span className="inline-flex items-center gap-2 min-w-0">
            <MemberAvatar src={r.picture_url} name={real || nick} size={28} />
            <span>{real || nick || "—"}</span>
            {real && nick && real !== nick && <span className="text-xs text-muted">{nick}</span>}
          </span>
        );
      },
    },
    { key: "tier", header: "等級", render: (r) => <TierBadge tier={r.tier || "general"} size="sm" /> },
    {
      key: "status",
      header: "狀態",
      // 「已封鎖官方帳號」與「後台停用」是兩件事,分開顯示(2026-09-16 之前兩者都寫成 suspended)
      render: (r) => (
        <span>
          {STATUS_LABEL[r.status || ""] || r.status}
          {r.is_following === false && (
            <span className="ml-1 text-xs text-muted">· 已封鎖</span>
          )}
        </span>
      ),
    },
    { key: "phone", header: "手機", render: (r) => r.phone || "—" },
    {
      key: "referral",
      header: "推薦來源",
      render: (r) => {
        const rb = r.referred_by;
        if (!rb || !rb.code) return "—";
        // 顯示顧問名(查不到對應碼時退而顯示碼字串本身)
        return <span className={rb.resolved ? "" : "text-muted"}>{rb.label || rb.code}</span>;
      },
    },
    { key: "spent", header: "總消費", render: (r) => `NT$ ${(r.total_spent || 0).toLocaleString()}` },
    { key: "last", header: "最後互動", render: (r) => r.last_interaction_at ? String(r.last_interaction_at).slice(0, 10) : "—" },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="members" />

      <div className="p-6">
        <div className="card mb-5 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="label">搜尋</label>
            <input
              className="input w-full"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="姓名 / 手機 / Email"
            />
          </div>
          <div>
            <label className="label">狀態</label>
            <select className="input min-w-32" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">全部</option>
              <option value="pending">待入會</option>
              <option value="active">正常</option>
              <option value="suspended">停用</option>
            </select>
          </div>
          <div>
            <label className="label">等級</label>
            <select className="input min-w-32" value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
              <option value="">全部</option>
              {Object.entries(TIER_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">推薦來源</label>
            <select className="input min-w-32" value={filterReferral} onChange={(e) => setFilterReferral(e.target.value)}>
              <option value="">全部</option>
              <option value="__none__">未填推薦碼</option>
              {referralSources.map((s) => (
                <option key={s.code} value={s.code}>
                  {(s.label || s.code)}({s.count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">標籤</label>
            <select className="input min-w-32" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
              <option value="">全部</option>
              {memberTags.map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.tag}({t.count})
                </option>
              ))}
            </select>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => navigate("/admin/members/new")} className="btn-primary">＋ 新增會員</button>
            <button onClick={() => navigate("/admin/members/duplicates")} className="btn-secondary">重複帳號</button>
            <button onClick={exportCsv} className="btn-secondary">匯出 CSV</button>
          </div>
        </div>

        {/* 選取工具列(勾起時冒出) */}
        {selected.size > 0 && (
          <div className="card mb-3 flex items-center gap-3 bg-sage-light/40 border border-sage">
            <div className="text-sm font-medium text-ink">
              已選 {selected.size} 位
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => {
                  const id = Array.from(selected)[0];
                  if (id) navigate(`/admin/members/${id}`);
                }}
                disabled={selected.size !== 1 || bulkRunning}
                className="btn-secondary text-sm disabled:opacity-40"
                title={selected.size === 1 ? "進詳情頁編輯" : "只勾 1 位才能用"}
              >
                編輯
              </button>
              <button
                onClick={() => bulkSetStatus("active")}
                disabled={bulkRunning}
                className="btn-secondary text-sm disabled:opacity-40"
              >
                啟用
              </button>
              <button
                onClick={() => bulkSetStatus("suspended")}
                disabled={bulkRunning}
                className="text-sm text-coral border border-coral/40 rounded px-3 py-1.5 hover:bg-coral/10 disabled:opacity-40"
              >
                停用
              </button>
              <button
                onClick={bulkDelete}
                disabled={bulkRunning}
                className="text-sm text-white bg-coral rounded px-3 py-1.5 hover:bg-coral/90 disabled:opacity-40"
                title="永久刪除,不可復原。有歷史會被擋下,可選擇強制刪"
              >
                刪除
              </button>
              <button
                onClick={() => setSelected(new Set())}
                disabled={bulkRunning}
                className="text-sm text-muted px-2 py-1.5 hover:text-ink disabled:opacity-40"
              >
                清除
              </button>
            </div>
          </div>
        )}

        <div className="card">
          <DataTable
            columns={columns}
            rows={rows}
            onRowClick={(r) => navigate(`/admin/members/${r.line_user_id}`)}
            emptyHint={loading ? "載入中…" : "尚無會員"}
            selectable
            getRowId={(r) => r.line_user_id || ""}
            selectedIds={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
          <div className="text-xs text-muted mt-3 text-right">共 {rows.length} 筆</div>
        </div>
      </div>
    </div>
  );
}
